import { HomeThemeProvider } from '@/contexts/HomeThemeContext';
import { useMyHomes } from '@/hooks/useMe';
import ThemePage from '@/pages/settings/ThemePage';
/**
 * ============================================================
 * KEJJA OS — App
 * Route dispatcher.
 * ============================================================
 */

import { Routes, Route, Navigate } from 'react-router-dom';

import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import FinanceLayout from '@/pages/finance/FinanceLayout';
import FinanceOverviewPage from '@/pages/finance/FinanceOverviewPage';
import IncomePage from '@/pages/finance/IncomePage';
import TransactionsPage from '@/pages/finance/TransactionsPage';
import MpesaPage from '@/pages/finance/MpesaPage';
import BudgetsPage from '@/pages/finance/BudgetsPage';
import CategoriesPage from '@/pages/finance/CategoriesPage';
import BillsPage from '@/pages/finance/BillsPage';
import FundsPage from '@/pages/finance/FundsPage';
import CreditPage from '@/pages/finance/CreditPage';
import RoutinesLayout from '@/pages/routines/RoutinesLayout';
import TodayPageV2 from '@/pages/routines/TodayPageV2';
import KidsPage from '@/pages/routines/KidsPage';
import ManagePage from '@/pages/routines/ManagePage';
import ComingSoonPage from '@/pages/ComingSoonPage';
import OnboardingPage from '@/pages/OnboardingPage';
import OnboardingCelebrationPage from '@/pages/OnboardingCelebrationPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import WelcomePage from '@/pages/WelcomePage';
import { EmailVerifiedGate } from '@/components/auth/EmailVerifiedGate';
import { WelcomeGate } from '@/components/auth/WelcomeGate';
import MealsLayout from '@/pages/meals/MealsLayout';
import MealsTodayPage from '@/pages/meals/TodayPage';
import MealsPlanPage from '@/pages/meals/PlanPageV2';
import MealsShoppingPage from '@/pages/meals/ShoppingPage';
import MealsRecipesPage from '@/pages/meals/RecipesPage';
import CalendarLayout from '@/pages/calendar/CalendarLayout';
import MonthView from '@/pages/calendar/MonthView';
import WeekView from '@/pages/calendar/WeekView';
import AgendaView from '@/pages/calendar/AgendaView';
import LibraryLayout from '@/pages/library/LibraryLayout';
import BrowsePage from '@/pages/library/BrowsePage';
import CommunityLayout from '@/pages/community/CommunityLayout';
import CommunityListPage from '@/pages/community/CommunityListPage';
import CommunityDetailLayout from '@/pages/community/CommunityDetailLayout';
import CommunityFeedPage from '@/pages/community/CommunityFeedPage';
import CommunityGoalsPage from '@/pages/community/CommunityGoalsPage';
import CommunityMembersPage from '@/pages/community/CommunityMembersPage';
import SettingsLayout from '@/pages/settings/SettingsLayout';
import ProfilePage from '@/pages/settings/ProfilePage';
import HouseholdPage from '@/pages/settings/HouseholdPage';
import PreferencesPage from '@/pages/settings/PreferencesPage';
import MembersPage from '@/pages/settings/MembersPage';
import SystemPage from '@/pages/system/SystemPage';
import PantryLayout from '@/pages/pantry/PantryLayout';
import PantryListPage from '@/pages/pantry/PantryListPage';
import PantryLowStockPage from '@/pages/pantry/PantryLowStockPage';
import PantryExpiringPage from '@/pages/pantry/PantryExpiringPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminOverviewPage from '@/pages/admin/AdminOverviewPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminHomesPage from '@/pages/admin/AdminHomesPage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { OnboardingGate } from '@/components/auth/OnboardingGate';
import { AppShell } from '@/components/layout/AppShell';


function HomeThemeWrapper({ children }: { children: React.ReactNode }) {
  const homesQ = useMyHomes();
  const homeId = homesQ.data?.homes?.[0]?.home?.id;
  return <HomeThemeProvider homeId={homeId}>{children}</HomeThemeProvider>;
}

function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverviewPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="homes" element={<AdminHomesPage />} />
            </Route>
            <Route path="/login" element={<LoginPage />} />

      {/* Onboarding (auth required but no home yet) */}
      <Route
        path="/verify-email" element={<ProtectedRoute><VerifyEmailPage /></ProtectedRoute>} />
        <Route path="/welcome" element={<ProtectedRoute><EmailVerifiedGate><WelcomePage /></EmailVerifiedGate></ProtectedRoute>} />
        <Route path="/onboarding"
        element={
          <ProtectedRoute>
            <EmailVerifiedGate><OnboardingPage /></EmailVerifiedGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/celebration"
        element={
          <ProtectedRoute>
            <EmailVerifiedGate><OnboardingCelebrationPage /></EmailVerifiedGate>
          </ProtectedRoute>
        }
      />

      {/* Protected + has-home — everything inside AppShell */}
      <Route
        element={
          <ProtectedRoute>
            <EmailVerifiedGate><WelcomeGate><OnboardingGate>
              <HomeThemeWrapper><AppShell /></HomeThemeWrapper>
            </OnboardingGate></WelcomeGate></EmailVerifiedGate>
          </ProtectedRoute>
        }
      >
        <Route path="/settings/theme" element={<ThemePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/finance" element={<FinanceLayout />}>
          <Route index element={<FinanceOverviewPage />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="income" element={<IncomePage />} />
          <Route path="mpesa" element={<MpesaPage />} />
          <Route path="bills" element={<BillsPage />} />
          <Route path="funds" element={<FundsPage />} />
          <Route path="credit" element={<CreditPage />} />
          <Route path="budgets" element={<BudgetsPage />} />
          <Route path="categories" element={<CategoriesPage />} />
        </Route>
        <Route path="/routines" element={<RoutinesLayout />}>
          <Route index element={<TodayPageV2 />} />
          <Route path="manage" element={<ManagePage />} />
          <Route path="kids" element={<KidsPage />} />
        </Route>
        <Route path="/meals" element={<MealsLayout />}>
              <Route index element={<MealsTodayPage />} />
              <Route path="plan" element={<MealsPlanPage />} />
              <Route path="shopping" element={<MealsShoppingPage />} />
              <Route path="recipes" element={<MealsRecipesPage />} />
            </Route>
        <Route path="/calendar" element={<CalendarLayout />}>
              <Route index element={<MonthView />} />
              <Route path="week" element={<WeekView />} />
              <Route path="agenda" element={<AgendaView />} />
            </Route>
        <Route path="/library" element={<LibraryLayout />}>
              <Route index element={<BrowsePage />} />
              <Route path="personal" element={<BrowsePage />} />
            </Route>
        <Route path="/community" element={<CommunityLayout />}>
              <Route index element={<CommunityListPage />} />
              <Route path=":communityId" element={<CommunityDetailLayout />}>
                <Route index element={<CommunityFeedPage />} />
                <Route path="goals" element={<CommunityGoalsPage />} />
                <Route path="members" element={<CommunityMembersPage />} />
              </Route>
            </Route>
        <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<ProfilePage />} />
              <Route path="home" element={<HouseholdPage />} />
              <Route path="preferences" element={<PreferencesPage />} />
              <Route path="members" element={<MembersPage />} />
            </Route>
        <Route path="/pantry" element={<PantryLayout />}>
              <Route index element={<PantryListPage />} />
              <Route path="low-stock" element={<PantryLowStockPage />} />
              <Route path="expiring" element={<PantryExpiringPage />} />
            </Route>
                        <Route path="/notifications/*" element={<NotificationsPage />} />
            <Route path="/system" element={<SystemPage />} />
        <Route path="/invitations" element={<ComingSoonPage />} />
        <Route path="/homes/:id" element={<ComingSoonPage />} />
        <Route path="/homes/new" element={<ComingSoonPage />} />
      </Route>

      {/* Root → dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Anything else → login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
